import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cross from '../assets/svgs/Cross';
import { buyerTypes } from '../utils/constants';
import InputField from './InputField';
import Select from './Select';
import Radio from './Radio';
import Button from './Button';
import useReCaptcha, { RecaptchaComponent } from '../utils/recaptcha';
import api from '../services/api';

export const RegistrationForm = ({ toggleRegistrationForm = () => { } }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: '',
    postCode: '',
    buyerType: 'Buyer Type',
    howDidYouHearAboutUs: 'How did you hear about us?'
  });
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const { recaptchaRef, executeRecaptcha } = useReCaptcha();
  const [enquireSource, setEnquireSource] = useState([
      "Billboards",
      "Builder Referral",
      "Digital H&L Collections Book",
      "Display Village",
      "Facebook",
      "Family/Friends",
      "Google",
      "Instagram",
      "Local Knowledge",
      "Local Paper",
      "Newsletter",
      "Newspaper Metro",
      "Openlot.com.au",
      "Other",
      "Radio",
      "Signage",
      "Website",
      "realestate.com.au",
  ]);

  useEffect(() => {
    const fetchProject = async () => {
      const project = await api.getProject();
      if (
        project.frontendEnquirySources &&
        project.frontendEnquirySources.length
      ) {
          setEnquireSource(
              project.frontendEnquirySources.map((x) => x.name)
          );
      }
    }
    fetchProject();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic email validation
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else {
      newErrors.email = 'Email is required';
    }

    // Postcode validation (Australian format)
    if (formData.postCode) {
      const postcodeRegex = /^[0-9]{4}$/;
      if (!postcodeRegex.test(formData.postCode)) {
        newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
      }
    } else {
      newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
    }

    // Buyer type validation
    if (formData.buyerType === 'Buyer Type') {
      newErrors.buyerType = 'Please select a buyer type';
    }

    // How did you hear about us validation
    if (formData.howDidYouHearAboutUs === 'How did you hear about us?') {
      newErrors.howDidYouHearAboutUs = 'Please select how you heard about us';
    }

    // Phone validation (basic)
    const phoneRegex = /^[0-9+\s()-]{8,15}$/;
    if (!phoneRegex.test(formData.mobilePhone)) {
      newErrors.mobilePhone = 'Please enter a valid phone number';
    }

    // Privacy policy check
    if (!isChecked) {
      newErrors.terms = 'You must agree to the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Get reCAPTCHA token using the new method with action parameter
        const token = await executeRecaptcha('registration_form');

        // Create form data for submission
        const formDataToSubmit = new URLSearchParams();
        formDataToSubmit.append('firstName', formData.firstName);
        formDataToSubmit.append('lastName', formData.lastName);
        formDataToSubmit.append('email', formData.email);
        formDataToSubmit.append('postCode', formData.postCode);
        formDataToSubmit.append('buyerType', formData.buyerType);
        formDataToSubmit.append('mobilePhone', formData.mobilePhone);
        formDataToSubmit.append('howDidYouHearAboutUs', formData.howDidYouHearAboutUs);

        // Add reCAPTCHA token
        formDataToSubmit.append('g-recaptcha-response', token);

        // API endpoint from environment variable
        const apiUrl = `${process.env.REACT_APP_API_URL}/register`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSubmit,
        });

        if (response.ok) {
          // Success handling
          setIsSubmitted(true);
          setIsSubmitting(false);

          // Reset form
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            postCode: '',
            buyerType: 'Buyer Type',
            mobilePhone: '',
            howDidYouHearAboutUs: 'How did you hear about us?'
          });
          setIsChecked(false);

          // Close modal after 3 seconds on successful submission
          setTimeout(() => {
            toggleRegistrationForm();
            navigate('/thank-you');
          }, 3000);
        } else {
          const errorData = await response.json();
          console.error('Form submission error:', errorData);
          setIsSubmitting(false);
          setErrors(prev => ({
            ...prev,
            general: errorData.message || 'Something went wrong. Please try again.'
          }));
        }
      } catch (error) {
        console.error('Form submission error:', error);
        setIsSubmitting(false);
        setErrors(prev => ({
          ...prev,
          general: 'reCAPTCHA verification failed. Please try again.'
        }));
      }
    }
  };

  return (
    <div className='bg-gableGreen fixed h-screen w-full left-0 right-0 top-0 z-50 px-6 sm:px-10 xl:px-59 pt-10 overflow-y-auto'>
      <div className='w-full'>
        <div onClick={toggleRegistrationForm} className='ml-auto cursor-pointer w-fit'>
          <Cross className='w-5 sm:w-6 xl:w-8' />
        </div>
      </div>
      <div className="w-full sm:w-85 lg:w-70 mx-auto mt-5 sm:mt-10 pb-10">
        <h1 className="text-honeysuckle font-tobias font-medium text-4xl xl:text-5xl mb-6 text-center tracking-wide">Banyan Hill</h1>

        <p className="text-honeysuckle text-base sm:text-xl mb-10 text-center font-medium">
          Don't miss out, call today on <span className="text-sycamore font-semibold text-lg sm:text-xl hover:underline">1300 326 197</span>
          <br />or register your interest below.
        </p>

        {isSubmitted ? (
          <div className="bg-sycamore bg-opacity-30 text-honeysuckle p-6 rounded-lg mb-10 w-full text-center shadow-lg">
            <h3 className="text-xl font-semibold mb-3">Thank you for registering your interest!</h3>
            <p className="text-base">A member of our team will be in touch with you shortly.</p>
          </div>
        ) : (
          <form className="flex flex-col gap-8 sm:gap-10 mt-10 p-6 sm:p-8">
            <RecaptchaComponent ref={recaptchaRef} />

            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              <div className="w-full">
                <InputField
                  placeholder="First Name"
                  variant="honeysuckle"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
                {errors.firstName && <span className="text-red-300 text-xs mt-1">{errors.firstName}</span>}
              </div>
              <div className="w-full">
                <InputField
                  placeholder="Last Name"
                  variant="honeysuckle"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
                {errors.lastName && <span className="text-red-300 text-xs mt-1">{errors.lastName}</span>}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              <div className="w-full">
                <InputField
                  placeholder="Email"
                  variant="honeysuckle"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
                {errors.email && <span className="text-red-300 text-xs mt-1">{errors.email}</span>}
              </div>
              <div className="w-full">
                <InputField
                  placeholder="Postcode (4 digits)"
                  variant="honeysuckle"
                  value={formData.postCode}
                  onChange={(e) => handleInputChange('postCode', e.target.value)}
                  maxLength="4"
                />
                {errors.postCode && <span className="text-red-300 text-xs mt-1">{errors.postCode}</span>}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              <div className="w-full">
                <Select
                  defaultValue={formData.buyerType}
                  options={[...buyerTypes]}
                  onChange={(value) => handleInputChange('buyerType', value)}
                  variant="enhanced"
                  mobileTitle="Select Buyer Type"
                />
                {errors.buyerType && <span className="text-red-300 text-xs mt-1">{errors.buyerType}</span>}
              </div>
              <div className="w-full">
                <InputField
                  placeholder="Phone"
                  variant="honeysuckle"
                  value={formData.mobilePhone}
                  onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                />
                {errors.mobilePhone && <span className="text-red-300 text-xs mt-1">{errors.mobilePhone}</span>}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              <div className="w-full">
                <Select
                  defaultValue={formData.howDidYouHearAboutUs}
                  options={enquireSource}
                  onChange={(value) => handleInputChange('howDidYouHearAboutUs', value)}
                  variant="enhanced"
                  mobileTitle="How did you hear about us?"
                />
                {errors.howDidYouHearAboutUs && <span className="text-red-300 text-xs mt-1">{errors.howDidYouHearAboutUs}</span>}
              </div>
              <div className="w-full">
                {/* Empty div to maintain layout balance */}
              </div>
            </div>

            <div className="flex gap-6 items-center">
              <div onClick={() => {
                setIsChecked(!isChecked);
                if (errors.terms) {
                  setErrors(prev => ({ ...prev, terms: '' }));
                }
              }}>
                <Radio variant="honeysuckle" checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
              </div>
              <label className="text-sm sm:text-base font-mori text-honeysuckle">
                I agree to the <Link className="underline text-sycamore hover:text-honeysuckle transition-colors" to="/our-policies" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
              </label>
            </div>
            {errors.terms && <span className="text-red-300 text-xs mt-1">{errors.terms}</span>}

            {errors.general && <div className="text-red-300 text-sm mt-2 text-center">{errors.general}</div>}
            <div className="mt-8 sm:mt-10 flex">
              <Button
                title={isSubmitting ? "Submitting..." : "REGISTER NOW"}
                className="sm:!px-12"
                onClick={handleSubmit}
                disabled={isSubmitting}
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationForm;